import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Layers, Lock, Server, Boxes } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const categoryIcons = [Server, Code2, Layers, Lock, Boxes];

const techGroups = [
  ['FastAPI', 'PostgreSQL', 'Celery', 'Redis'],
  ['React', 'TypeScript', 'Tailwind CSS'],
  ['JSON-SOAP', 'Zimbra Admin API'],
  ['JWT', 'bcrypt', 'Fernet encryption'],
  ['Docker Compose', 'Nginx']
];

const TechStack = () => {
  const { t } = useTranslation('zimbra-admin-panel');
  const highlights = t('techStack.highlights', { returnObjects: true }) as Array<{ title: string; description: string }>;
  const categories = t('techStack.categories', { returnObjects: true }) as Array<{ category: string }>;

  return (
    <section className="py-24 bg-white dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('techStack.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            {t('techStack.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mb-16 bg-gradient-to-br from-indigo-50 to-cyan-50 dark:from-slate-800 dark:to-slate-800 rounded-2xl p-8 border border-indigo-100 dark:border-slate-700"
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
            {categories.map((cat, index) => {
              const Icon = categoryIcons[index] ?? Server;
              const technologies = techGroups[index] ?? [];
              return (
                <React.Fragment key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-4 mx-auto shadow-lg">
                      <Icon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                      {cat.category}
                    </h3>
                    <div className="space-y-2">
                      {technologies.map((technology, techIndex) => (
                        <div
                          key={techIndex}
                          className="inline-block px-3 py-1 bg-white dark:bg-slate-900 rounded-full text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700 m-1"
                        >
                          {technology}
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {index < categories.length - 1 && (
                    <div className="hidden md:flex items-center justify-center">
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 + 0.2 }}
                        className="w-8 h-0.5 bg-gradient-to-r from-indigo-500 to-cyan-500"
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {highlights.map((highlight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center p-6"
            >
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {highlight.title}
              </h4>
              <p className="text-gray-600 dark:text-gray-400">
                {highlight.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechStack;
